import { useCallback, useEffect, useRef, useState } from 'react';

import { HttpClient } from './api.client.js';
import { readErrorMessage, readNdjson } from './ndjson.js';
import { type CameraVisionSettings, type CameraZoneType } from '../models/index.js';

export type LiveVisionStage = 'raw' | 'preprocessed' | 'detection' | 'pose' | 'result';

export interface LiveVisionObject {
  label: string;
  confidence: number;
  box: [number, number, number, number];
  trackId: number | null;
}

export interface LiveVisionPerson {
  trackId: number | null;
  confidence: number;
  visibleKeypoints: number;
  posture: string;
  zoneType: CameraZoneType | null;
  patientScore: number | null;
}

export interface VisionStageView {
  width: number;
  height: number;
  image: string;
  latencyMs: number;
  lighting: 'day' | 'ir';
  objects: LiveVisionObject[];
  people: LiveVisionPerson[];
  motion: Array<[number, number, number, number]>;
  patientTrackId: number | null;
}

export interface StartVisionReplayBody {
  sampleFps: number;
  tracking: boolean;
  settings?: CameraVisionSettings;
}

export interface ReplaySource {
  kind: 'capture' | 'snippet';
  id: string;
}

const REPLAY_PATHS: Record<ReplaySource['kind'], string> = {
  capture: 'activity/captures',
  snippet: 'activity/snippets',
};

export interface VisionReplayFrame {
  frameIndex: number;
  timestampMs: number;
  stages: Record<LiveVisionStage, VisionStageView>;
}

type VisionReplayMessage = VisionReplayFrame | { error: string };

export type VisionReplayStatus = 'idle' | 'running' | 'done' | 'error';

function isErrorMessage(message: VisionReplayMessage): message is { error: string } {
  return 'error' in message;
}

export class VisionReplayApi {
  static async stream(
    source: ReplaySource,
    body: StartVisionReplayBody,
    signal: AbortSignal,
    onMessage: (message: VisionReplayMessage) => void,
  ): Promise<void> {
    const token = await HttpClient.getInstance().getToken();
    const url = `${import.meta.env.VITE_API_BASE_URL}/${REPLAY_PATHS[source.kind]}/${source.id}/vision-replay`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
      signal,
    });
    if (!response.ok) {
      throw new Error(await readErrorMessage(response, 'The replay could not start'));
    }
    await readNdjson(response, onMessage);
  }
}

export function useVisionReplay(source: ReplaySource | null) {
  const [status, setStatus] = useState<VisionReplayStatus>('idle');
  const [frames, setFrames] = useState<VisionReplayFrame[]>([]);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const handleMessage = useCallback((message: VisionReplayMessage) => {
    if (isErrorMessage(message)) {
      setError(message.error);
      setStatus('error');
      return;
    }
    setFrames((current) => [...current, message]);
  }, []);

  const start = useCallback((body: StartVisionReplayBody) => {
    if (!source) {
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setFrames([]);
    setError(null);
    setStatus('running');
    VisionReplayApi.stream(source, body, controller.signal, handleMessage)
      .then(() => setStatus((current) => (current === 'error' ? current : 'done')))
      .catch((reason: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        setError(reason instanceof Error ? reason.message : 'The replay stopped unexpectedly');
        setStatus('error');
      });
  }, [source, handleMessage]);

  useEffect(() => () => abortRef.current?.abort(), [source]);

  return { status, frames, error, start };
}
